import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  Input,
  OnInit,
  Self,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NgControl } from '@angular/forms';
import { DatepickerRangeModel, DatepickerRangeOutput } from '../../common/models';
import { AutoUnsubscribe } from '../../common/decorators';
import { LOCALE_CODE_LIST, MODIFIED_DATE_TIMEPICKER_CONFIG } from '../../common/constants/date-config';
import { Subscription } from 'rxjs';
import { PickerType } from '@danielmoncada/angular-datetime-picker/lib/date-time/date-time.class';
import { filter } from 'rxjs/operators';
import { AppFacade } from '../../store';
import { DateTimeAdapter, OWL_DATE_TIME_FORMATS, OwlDateTimeComponent } from '@danielmoncada/angular-datetime-picker';
import { endOfDay, getHours, getMinutes, startOfDay } from 'date-fns';
import { getDateAsUTC, IconNames } from '../../common';

@Component({
  selector: 'ui-date-time-range',
  templateUrl: './date-time-range.component.html',
  styleUrls: ['./date-time-range.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: OWL_DATE_TIME_FORMATS,
      useValue: MODIFIED_DATE_TIMEPICKER_CONFIG,
    },
  ],
})
@AutoUnsubscribe
export class DateTimeRangeComponent implements OnInit, ControlValueAccessor {
  @Input()
  label: string;

  @Input() pickerType: PickerType = 'both';
  @Input() initialValue: DatepickerRangeModel = new DatepickerRangeModel();
  @Input() removeBottomPadding?: boolean = false;

  @ViewChild('datetimePicker') datetimePicker: OwlDateTimeComponent<unknown>;

  @ContentChild('prefix') prefix: TemplateRef<HTMLElement>;
  @ContentChild('suffix') suffix: TemplateRef<HTMLElement>;

  iconNames = IconNames;
  dateTimeRangeControl = new UntypedFormControl();

  private subscriptions: Subscription[] = [];

  constructor(
    @Self() public ngControl: NgControl,
    public dateTimeAdapter: DateTimeAdapter<Date>,
    private appFacade: AppFacade,
  ) {
    ngControl.valueAccessor = this;
  }

  ngOnInit(): void {
    this.initDateTimeRangeValueSubscription();
    const langSubscription = this.appFacade.currentLanguage$.pipe(filter(Boolean)).subscribe((data: string) => {
      this.dateTimeAdapter.setLocale(LOCALE_CODE_LIST[data]);
    });
    this.subscriptions.push(
      langSubscription,
      this.dateTimeRangeControl.valueChanges
        .pipe(filter(() => this.pickerType === 'calendar'))
        .subscribe((data: DatepickerRangeOutput) => {
          this.onChange({
            start: startOfDay(new Date(data[0])),
            end: endOfDay(new Date(data[1])),
          });
        }),
    );
  }

  registerOnChange(fn: () => DatepickerRangeOutput): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  writeValue(dateTimeRange: DatepickerRangeOutput): void {
    const startDateForPicker = dateTimeRange?.start ? dateTimeRange?.start : null;
    const endDateForPicker = dateTimeRange?.end ? dateTimeRange?.end : null;

    this.dateTimeRangeControl.setValue([startDateForPicker, endDateForPicker], { emitEvent: false });
  }

  private onChange: (value: DatepickerRangeOutput) => DatepickerRangeOutput;

  private onTouched: () => void;

  private initDateTimeRangeValueSubscription(): void {
    const subscription = this.dateTimeRangeControl.valueChanges
      .pipe(filter(() => this.pickerType !== 'calendar'))
      .subscribe((dateTimeRange: [Date, Date]) => {
        const datePickerRange = new DatepickerRangeOutput();

        if (getHours(dateTimeRange[1]) === 0 && getMinutes(dateTimeRange[1]) === 0) {
          dateTimeRange[1] = endOfDay(dateTimeRange[1]);
        }

        datePickerRange.start = getDateAsUTC(new Date(dateTimeRange[0]));
        datePickerRange.end = getDateAsUTC(new Date(dateTimeRange[1]));
        this.onChange(datePickerRange);
      });

    this.subscriptions.push(subscription);
  }

  resetDatepicker(): void {
    this.writeValue(new DatepickerRangeOutput(this.initialValue));
    this.onChange(new DatepickerRangeOutput(this.initialValue));
  }
}
